import CronoError from 'crono-fe-common/types/error';

export const DYNAMICS_CONNECT_ERRORS = {
  generic:
    'Crono couldn’t connect to Dynamics 365. Please try again or contact support.',
  noEnvironment:
    'Crono couldn’t find a Dynamics 365 environment for this account. Sign in with the account your team uses for Dynamics 365, or ask your Dynamics 365 admin for access.',
  signInExpired: 'Your Microsoft sign-in expired. Please connect again.',
  environmentUsedByAnotherWorkspace:
    'This Dynamics 365 environment is already connected to another Crono workspace. Contact support.',
  teamOnAnotherEnvironment:
    'Your team uses a different Dynamics 365 environment. Pick the same one.',
  userLinkedToAnotherCronoUser:
    'This Dynamics 365 account is already connected to another Crono user.',
  noEnvironmentAccess:
    'Your Microsoft account can’t access this Dynamics 365 environment. Ask your Dynamics 365 admin for a license and a security role.',
  adminApprovalNeeded:
    'Your Microsoft admin needs to approve Crono once for your organization. Ask them to approve it, then connect again.',
  dynamicsNotActive:
    'Dynamics 365 isn’t active in this Microsoft organization. Sign in with the account your team uses for Dynamics 365.',
} as const;

/**
 * The back end returns these errors with fixed messages, and the codes alone
 * don't tell them apart (three of them are `EntityNotConsistent`).
 */
const BACKEND_MESSAGE_ERRORS: [fragment: string, message: string][] = [
  [
    'different from the other users',
    DYNAMICS_CONNECT_ERRORS.teamOnAnotherEnvironment,
  ],
  [
    'already used by another subscription',
    DYNAMICS_CONNECT_ERRORS.environmentUsedByAnotherWorkspace,
  ],
  [
    'already integrated with the same Dynamics account',
    DYNAMICS_CONNECT_ERRORS.userLinkedToAnotherCronoUser,
  ],
  [
    'cannot access this environment',
    DYNAMICS_CONNECT_ERRORS.noEnvironmentAccess,
  ],
];

/** The copy to show for a failed `Dynamics/environments` or `Dynamics/callback_oauth` call. */
export const getDynamicsBackendErrorMessage = (error: CronoError): string => {
  const errors = error.response?.data?.errors ?? [];

  for (const { message } of errors) {
    const match = BACKEND_MESSAGE_ERRORS.find(([fragment]) =>
      message?.includes(fragment),
    );
    if (match) return match[1];
  }

  // A code Microsoft rejected: expired, already used or malformed.
  if (errors.some(({ code }) => code === 'IntegrationNotValidError')) {
    return DYNAMICS_CONNECT_ERRORS.signInExpired;
  }

  return DYNAMICS_CONNECT_ERRORS.generic;
};

/**
 * The copy to show when Microsoft redirects back with `error` instead of a
 * code. A user who hit "Need admin approval" (AADSTS90094) and went back gets
 * the same `access_denied` as a plain cancel, so the message covers both.
 */
export const getDynamicsMicrosoftErrorMessage = (
  error: string,
  errorDescription: string | null,
): string => {
  // The organization has no Dynamics service, so Microsoft stops before consent.
  if (errorDescription?.includes('AADSTS650052')) {
    return DYNAMICS_CONNECT_ERRORS.dynamicsNotActive;
  }
  if (error === 'access_denied') {
    return DYNAMICS_CONNECT_ERRORS.adminApprovalNeeded;
  }
  return DYNAMICS_CONNECT_ERRORS.generic;
};
